"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { signIn } from "next-auth/react";
import { isNativeAuthReturnUrl } from "@/lib/native-auth-return";
import { POST_SIGN_IN_PATH } from "@/lib/post-sign-in";
import { cancelNativeSignIn, markDeepLinkHandled, wasDeepLinkHandled } from "@/lib/native-auth-state";

/**
 * Catches the `/native/auth?code=…` App Link after Google sign-in completed in
 * the system browser, and trades the code for a session in THIS WebView's
 * cookie jar — the one the browser could not write to.
 *
 * Also owns re-arming the login form's in-flight guard (native-auth-state.ts)
 * once a Custom Tab is known to be closed without a completed sign-in — see
 * the `browserFinished` listener below. This lives here, not in the login
 * form, because only this component's deep-link listener can tell a genuine
 * user cancellation apart from the tab closing as a side effect of our own
 * successful redemption.
 *
 * Mounted app-wide rather than on /login because the WebView may have
 * navigated elsewhere while the browser was in front.
 */
export function NativeAuthBridge() {
  const router = useRouter();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // markDeepLinkHandled() records that the return link was recognized
    // for the CURRENT attempt, before the `Browser.close()` call below.
    // Re-verified against @capacitor/browser 8.0.4: BrowserPlugin.java's
    // close() no longer calls into Browser.java directly — it now starts
    // BrowserControllerActivity (a new singleTask Activity added in 8.x)
    // with a `close` extra; BrowserControllerActivity.onNewIntent() brings
    // that Activity forward and finishes it, clearing the Custom Tab above
    // it in the task. Browser.java's BROWSER_FINISHED is still raised from
    // a single place — handleGroupCompletion() — driven by EventGroup
    // counting TAB_SHOWN/TAB_HIDDEN and the plugin's own bindService()/
    // unbindService() calls on app resume/pause, and handleGroupCompletion()
    // still has no branch for who initiated the close (its own comment:
    // "there is no single point to fire this"). So the property this guard
    // actually depends on — that `browserFinished` cannot be told apart from
    // a user dismissal — still holds structurally, even though the call
    // path that reaches it changed. Whether Chrome actually emits
    // TAB_HIDDEN when the tab is cleared via this new Activity-finish path,
    // as opposed to a user-driven close inside the tab itself, is Custom
    // Tabs Service behaviour this plugin's source doesn't settle either
    // way — but the guard is correct regardless of the outcome: if the
    // event fires, this flag suppresses it; if it doesn't, there was
    // nothing to suppress. Without this flag, a self-triggered event (if
    // one arrives) would be indistinguishable from a genuine cancellation
    // and would re-arm the guard out from under an in-flight — or
    // just-succeeded — redemption. (iOS does NOT fire `browserFinished` for
    // a programmatic close, unchanged at 8.0.4: BrowserPlugin.swift's
    // close() dismisses the presented view controller directly via
    // `bridge?.viewController?.dismiss(animated:)` and never calls
    // notifyListeners; only the SFSafariViewController delegate's
    // user-dismissal callbacks — safariViewControllerDidFinish,
    // presentationControllerDidDismiss — do. Marking it is harmless there
    // either way, so one implementation covers both platforms.)
    //
    // This lives in native-auth-state.ts, not a local variable, because
    // these listeners are set up once for the life of the mount and are
    // reused across every attempt — a plain closure variable here would
    // latch permanently on the first deep link and never separate one
    // attempt's cancellation from another's. startNativeSignIn() resets it
    // at the top of each new attempt; cancelNativeSignIn() resets it at the
    // end of any attempt that didn't succeed, so it never outlives the
    // attempt it describes.
    //
    // The match itself is delegated to native-auth-return.ts so the path this
    // listener waits for and the path the mint route redirects to cannot drift
    // apart — and so it stays a parsed origin-and-pathname comparison rather than the
    // `startsWith` a custom scheme got away with. Every OTHER rakemori.com App
    // Link (an item page shared into the app, say) reaches this listener too
    // and must fall straight through.
    const handleUrl = async (url: string) => {
      if (!isNativeAuthReturnUrl(url, window.location.origin)) return;
      markDeepLinkHandled();

      const code = new URL(url).searchParams.get("code");
      const verifier = sessionStorage.getItem("native-auth-verifier");
      sessionStorage.removeItem("native-auth-verifier");

      await Browser.close().catch(() => {});

      if (!code || !verifier) {
        // Deep link fired but wasn't redeemable (malformed or unexpected) —
        // this is a failure, not a success, so unlike the redeemed path
        // below, re-arm rather than leaving the button permanently dead.
        //
        // Logged because this branch is otherwise silent: the user is simply
        // returned to the login screen with no indication of why. Capacitor
        // forwards console output to logcat, which is the only window into a
        // release WebView.
        console.error(
          `[native-auth] deep link not redeemable: code=${code ? "present" : "MISSING"} verifier=${verifier ? "present" : "MISSING"}`,
        );
        cancelNativeSignIn();
        return;
      }

      const result = await signIn("native-code", { code, verifier, redirect: false });
      if (result?.error) {
        console.error(`[native-auth] redemption rejected: ${result.error}`);
        cancelNativeSignIn();
        return;
      }
      // NOT "/". The apex used to resolve onward to the library, and this line
      // relied on it; src/app/page.tsx now keeps a signed-in visitor on the
      // marketing page, which left a user who had just signed in reading the
      // landing copy inside the app. See post-sign-in.ts.
      router.push(POST_SIGN_IN_PATH);
    };

    const urlListener = App.addListener("appUrlOpen", ({ url }) => void handleUrl(url));

    // A listener alone loses the code on a COLD start. Verified on a Pixel 6a:
    // Capacitor fires `appUrlOpen` about 2ms after "App started", while this
    // WebView is still fetching the app over the network and has registered
    // nothing — so the event, and the auth code with it, is dropped and the
    // user lands back on the login screen with no error.
    //
    // That is the common case, not an edge one: the Custom Tab covers the app
    // for the length of a Google sign-in, and Android is free to kill the
    // backgrounded process at any point. `getLaunchUrl()` asks for the URL the
    // app was launched with, which is the only way to see a deep link that
    // arrived before this code existed. Re-verified at @capacitor/app 8.1.1:
    // AppPlugin.java's handleOnNewIntent() still calls
    // notifyListeners(EVENT_URL_OPEN, ret, true) — the retain-until-consumed
    // flag is unchanged, which is what makes cold-start recovery possible
    // at all.
    //
    // Both paths funnel through `handleUrl`, and it is safe to run twice: the
    // verifier is removed on first use, so a second pass finds nothing and
    // re-arms rather than redeeming again.
    void App.getLaunchUrl()
      .then((launch) => {
        if (launch?.url) void handleUrl(launch.url);
      })
      .catch(() => {});

    // Fires when the user dismisses the Custom Tab/Safari view — including,
    // per the reasoning above, as a side effect of this component's own
    // `Browser.close()` call on Android. Only treat it as a cancellation
    // when this attempt's deep link never arrived: that is the case where
    // `nativeSignInInFlight` would otherwise be stuck `true` for the rest of
    // the WebView's life, leaving the button permanently dead.
    const finishedListener = Browser.addListener("browserFinished", () => {
      if (wasDeepLinkHandled()) return;
      // Deliberately does NOT clear the verifier. When Android closes the tab
      // in order to hand off the App Link, this event can arrive BEFORE
      // `appUrlOpen` reaches the WebView's JS — the guard above only covers
      // our own `Browser.close()`, which runs after markDeepLinkHandled().
      // Clearing here wiped the verifier out from under a redemption that was
      // still on its way, and the deep link then found nothing and returned
      // to the login screen with no error. Observed on a Pixel 6a.
      //
      // Leaving a stale verifier behind is harmless: it is random, useless
      // without a code minted against its hash, and the next attempt
      // overwrites it. Re-arming the guard is the part that actually matters.
      cancelNativeSignIn();
    });

    return () => {
      void urlListener.then((handle) => handle.remove());
      void finishedListener.then((handle) => handle.remove());
    };
  }, [router]);

  return null;
}
